import { useEffect, useState } from 'react'
import Button from '../ui/Button'
import FormError from '../ui/FormError'
import Input from '../ui/Input'
import { useLang } from '../../contexts/LangContext'

export interface PropertyFormValues {
  label: string
  propertyType: string
  addressLine1: string
  addressLine2: string
  city: string
  postalCode: string
  region: string
  floor: string
  sizeSqm: string
  bedrooms: string
  bathrooms: string
  accessInstructions: string
  notes: string
  hasElevator: boolean
  isDefault: boolean
}

const defaults: PropertyFormValues = {
  label: '',
  propertyType: 'residential',
  addressLine1: '',
  addressLine2: '',
  city: 'Athens',
  postalCode: '',
  region: '',
  floor: '',
  sizeSqm: '',
  bedrooms: '',
  bathrooms: '',
  accessInstructions: '',
  notes: '',
  hasElevator: false,
  isDefault: false,
}

const typeLabels = {
  residential: { el: 'Κατοικία', en: 'Residential' },
  office: { el: 'Γραφείο', en: 'Office' },
  airbnb: { el: 'Airbnb', en: 'Airbnb' },
  other: { el: 'Άλλο', en: 'Other' },
} as const

export function buildPropertyFormValues(values?: Partial<PropertyFormValues>): PropertyFormValues {
  return { ...defaults, ...values }
}

export default function PropertyForm({
  mode,
  initialValues,
  loading,
  errors,
  onSubmit,
  onCancel,
}: {
  mode: 'create' | 'edit'
  initialValues?: Partial<PropertyFormValues>
  loading?: boolean
  errors: string[]
  onSubmit: (values: PropertyFormValues) => Promise<void> | void
  onCancel?: () => void
}) {
  const { locale, t } = useLang()
  const [values, setValues] = useState<PropertyFormValues>(buildPropertyFormValues(initialValues))

  useEffect(() => {
    setValues(buildPropertyFormValues(initialValues))
  }, [initialValues])

  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
            {mode === 'create' ? t('Νέο ακίνητο', 'New property') : t('Επεξεργασία ακινήτου', 'Edit property')}
          </p>
          <h2 className="mt-2 text-2xl font-bold text-slate-950">
            {mode === 'create' ? t('Πρόσθεσε μια νέα διεύθυνση', 'Add a new address') : t('Ενημέρωσε τα στοιχεία του ακινήτου', 'Update this property')}
          </h2>
        </div>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('Κλείσιμο', 'Close')}
          </Button>
        )}
      </div>

      <FormError errors={errors} />

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Input
          label={t('Όνομα', 'Label')}
          value={values.label}
          onChange={(event) => setValues((current) => ({ ...current, label: event.target.value }))}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="property-type" className="text-sm font-medium text-slate-700">
            {t('Τύπος ακινήτου', 'Property type')}
          </label>
          <select
            id="property-type"
            value={values.propertyType}
            onChange={(event) => setValues((current) => ({ ...current, propertyType: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 transition-colors hover:border-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.entries(typeLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label[locale]}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <Input
            label={t('Διεύθυνση', 'Address')}
            value={values.addressLine1}
            onChange={(event) => setValues((current) => ({ ...current, addressLine1: event.target.value }))}
          />
        </div>

        <div className="md:col-span-2">
          <Input
            label={t('Επιπλέον στοιχεία διεύθυνσης', 'Address line 2')}
            value={values.addressLine2}
            onChange={(event) => setValues((current) => ({ ...current, addressLine2: event.target.value }))}
          />
        </div>

        <Input
          label={t('Πόλη', 'City')}
          value={values.city}
          onChange={(event) => setValues((current) => ({ ...current, city: event.target.value }))}
        />
        <Input
          label={t('Ταχυδρομικός κώδικας', 'Postal code')}
          inputMode="numeric"
          value={values.postalCode}
          onChange={(event) => setValues((current) => ({ ...current, postalCode: event.target.value.replace(/\D/g, '').slice(0, 5) }))}
        />

        <Input
          label={t('Περιοχή / Νομός', 'Region')}
          value={values.region}
          onChange={(event) => setValues((current) => ({ ...current, region: event.target.value }))}
        />
        <Input
          label={t('Όροφος', 'Floor')}
          inputMode="numeric"
          value={values.floor}
          onChange={(event) => setValues((current) => ({ ...current, floor: event.target.value.replace(/[^\d-]/g, '') }))}
        />

        <Input
          label={t('Τετραγωνικά μέτρα', 'Size (sqm)')}
          inputMode="numeric"
          value={values.sizeSqm}
          onChange={(event) => setValues((current) => ({ ...current, sizeSqm: event.target.value.replace(/\D/g, '') }))}
        />
        <Input
          label={t('Υπνοδωμάτια', 'Bedrooms')}
          inputMode="numeric"
          value={values.bedrooms}
          onChange={(event) => setValues((current) => ({ ...current, bedrooms: event.target.value.replace(/\D/g, '') }))}
        />

        <Input
          label={t('Μπάνια', 'Bathrooms')}
          inputMode="numeric"
          value={values.bathrooms}
          onChange={(event) => setValues((current) => ({ ...current, bathrooms: event.target.value.replace(/\D/g, '') }))}
        />

        <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={values.hasElevator}
            onChange={(event) => setValues((current) => ({ ...current, hasElevator: event.target.checked }))}
          />
          {t('Υπάρχει ασανσέρ', 'Elevator available')}
        </label>

        <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={values.isDefault}
            onChange={(event) => setValues((current) => ({ ...current, isDefault: event.target.checked }))}
          />
          {t('Ορισμός ως προεπιλογή', 'Set as default')}
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-sm font-medium text-slate-700">{t('Οδηγίες πρόσβασης', 'Access instructions')}</span>
          <textarea
            rows={3}
            value={values.accessInstructions}
            onChange={(event) => setValues((current) => ({ ...current, accessInstructions: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 transition-colors hover:border-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-sm font-medium text-slate-700">{t('Σημειώσεις', 'Notes')}</span>
          <textarea
            rows={4}
            value={values.notes}
            onChange={(event) => setValues((current) => ({ ...current, notes: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 transition-colors hover:border-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </label>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('Άκυρο', 'Cancel')}
          </Button>
        )}
        <Button
          type="button"
          size="lg"
          loading={loading}
          onClick={() => onSubmit(values)}
        >
          {mode === 'create' ? t('Αποθήκευση ακινήτου', 'Save property') : t('Αποθήκευση αλλαγών', 'Save changes')}
        </Button>
      </div>
    </section>
  )
}
